import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface StatusBadgeProps {
  status: "connected" | "disconnected" | "error";
  children: React.ReactNode;
}

function StatusBadge({ status, children }: StatusBadgeProps) {
  const statusStyles = {
    connected: "bg-green-100 text-green-800",
    disconnected: "bg-gray-100 text-gray-600",
    error: "bg-red-100 text-red-800",
  };

  const dotColors = {
    connected: "#18C963",
    disconnected: "#9CA3AF",
    error: "#EF4444",
  };

  return (
    <div
      className={cn(
        "inline-flex h-10 items-center gap-2.5 rounded-md px-2 py-1 text-base font-normal leading-snug",
        statusStyles[status],
      )}
    >
      <svg
        width="8"
        height="8"
        viewBox="0 0 8 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="shrink-0"
      >
        <circle cx="4" cy="4" r="4" fill={dotColors[status]} />
      </svg>
      {children}
    </div>
  );
}

interface IntegrationCardProps {
  name: string;
  icon: string | React.ReactNode;
  iconAlt?: string;
  status: "connected" | "disconnected" | "error";
  statusLabel: string;
  onConnect?: () => void;
  onManage?: () => void;
  loading?: boolean;
  className?: string;
}

function IntegrationCard({
  name,
  icon,
  iconAlt = "",
  status,
  statusLabel,
  onConnect,
  onManage,
  loading = false,
  className,
}: IntegrationCardProps) {
  return (
    <Card variant="plain" className={cn("w-full min-w-[600px]", className)}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 p-6">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-4">
            {typeof icon === "string" ? (
              <img
                src={icon}
                alt={iconAlt}
                className="h-10 w-10 rounded-md object-cover"
              />
            ) : (
              <div className="h-10 w-10 flex items-center justify-center">
                {icon}
              </div>
            )}
            <h3 className="text-lg font-semibold leading-snug text-gray-800 m-0">
              {name}
            </h3>
          </div>
          <StatusBadge status={status}>{statusLabel}</StatusBadge>
        </div>
        <div className="flex items-center gap-2">
          {status === "disconnected" ? (
            <Button
              intent="primary"
              variant="solid"
              size="md"
              onClick={onConnect}
              disabled={loading}
              className="shrink-0 px-4"
            >
              {loading ? "Connecting..." : "Connect"}
            </Button>
          ) : (
            <>
              {onManage && (
                <Button
                  intent="secondary"
                  variant="solid"
                  size="md"
                  onClick={onManage}
                  disabled={loading}
                  className="shrink-0 px-4"
                >
                  Manage
                </Button>
              )}
            </>
          )}
        </div>
      </CardHeader>
    </Card>
  );
}

export { IntegrationCard, StatusBadge };
export type { IntegrationCardProps, StatusBadgeProps };
